/**
 * Cache Status Badge - Simplified version
 * Shows: Solar Availability + Data Quality
 */

import React from 'react';
import { Sun, Zap, CheckCircle } from 'lucide-react';
import { CacheInfo } from '../../types/solar';

interface CacheStatusBadgeProps {
  cacheInfo: CacheInfo | null;
  isLoading?: boolean;
  dataQuality?: string;  // From buildingInsights.imageryQuality
  solarAvailable?: boolean;  // If solar data was found
}

export default function CacheStatusBadge({ 
  cacheInfo, 
  isLoading,
  dataQuality = 'HIGH',
  solarAvailable = false
}: CacheStatusBadgeProps): JSX.Element {
  // Don't show if no data
  if (!cacheInfo && !isLoading && !solarAvailable) {
    return <></>;
  }

  // Loading state
  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '8px 12px',
        background: '#f3f4f6',
        borderRadius: 6,
        border: '2px solid #EBFD03',
      }}>
        <Sun className="h-4 w-4 text-gray-400 animate-pulse" />
        <span style={{ fontSize: 13, fontWeight: 600, color: '#6b7280' }}>
          Checking solar availability...
        </span>
      </div>
    );
  }

  if (!solarAvailable) return <></>;

  const isGCSCache = cacheInfo?.cacheHit || false;
  const isUploading = cacheInfo?.uploadStatus === 'in_progress';

  // Quality badge color
  const qualityColor = dataQuality === 'HIGH' ? '#10b981' : 
                       dataQuality === 'MEDIUM' ? '#f59e0b' : '#6b7280';

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: 6,
      padding: '10px 12px',
      background: '#f0fdf4',
      borderRadius: 6,
      border: `2px solid ${qualityColor}`,
    }}>
      {/* Solar Available Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <CheckCircle className="h-4 w-4" style={{ color: qualityColor }} />
          <span style={{ 
            fontSize: 13, 
            fontWeight: 700, 
            color: '#1f2937',
          }}>
            Solar Data Available
          </span>
        </div>
        {isGCSCache && <Zap className="h-3 w-3 text-yellow-500" />}
      </div>

      {/* Data Quality */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 6,
        paddingLeft: 22,
      }}>
        <span style={{ fontSize: 11, color: '#6b7280' }}>
          Quality:
        </span>
        <span style={{ 
          fontSize: 11, 
          fontWeight: 600,
          color: qualityColor,
        }}>
          {dataQuality}
        </span>
        {isGCSCache && (
          <span style={{ 
            fontSize: 10, 
            color: '#16a34a',
            marginLeft: 'auto',
          }}>
            • Cached
          </span>
        )}
        {isUploading && (
          <span style={{ 
            fontSize: 10, 
            color: '#f59e0b',
            marginLeft: 'auto',
          }}>
            • Uploading
          </span>
        )}
      </div>
    </div>
  );
}

